import {
  renameTask as renameTaskRequest,
  setTaskDescription as setTaskDescriptionRequest,
} from '@/lib/api/generated/api';
import { expectStatus } from '@/lib/api/client';
import { useOptimisticTaskPatch } from './useOptimisticTaskPatch';

export interface RenameTaskInput {
  workspaceId: string;
  taskId: string;
  title: string;
}

/** Give a task a new title. Trimming and bounds are the server's answer, not a guess. */
export function useRenameTask() {
  return useOptimisticTaskPatch<RenameTaskInput>({
    send: async ({ workspaceId, taskId, title }) =>
      expectStatus(
        await renameTaskRequest(workspaceId, taskId, { title }),
        200,
        'The title could not be saved.',
      ),
    optimistic: ({ title }) => ({ title }),
    // The title comes back trimmed.
    settled: (task) => ({ title: task.title, description: task.description }),
  });
}

export interface SetTaskDescriptionInput {
  workspaceId: string;
  taskId: string;
  /** `null` clears the description — an absence said, not one left out. */
  description: string | null;
}

/** Replace a task's description, or clear it. */
export function useSetTaskDescription() {
  return useOptimisticTaskPatch<SetTaskDescriptionInput>({
    send: async ({ workspaceId, taskId, description }) =>
      expectStatus(
        await setTaskDescriptionRequest(workspaceId, taskId, { description }),
        200,
        'The description could not be saved.',
      ),
    optimistic: ({ description }) => ({ description }),
    // A description of nothing but spaces comes back as `null`.
    settled: (task) => ({ title: task.title, description: task.description }),
  });
}
